import { ReactNode, useEffect } from 'react';
import { Icon } from 'hfs-common-ui';
import { testIds } from 'core/test-ids';
import * as S from './styled';
import Portal from '../Portal/Portal';

type Props = {
  /**
   * The content that will be passed into the modal.
   */
  children: ReactNode;
  /*
   * The title of the modal.
   * */
  title: string | ReactNode;
  /**
   * If the modal is open or not.
   */
  isOpen?: boolean;
  /**
   *  This function is need for closing the modal after its been opened.
   */
  close?: () => void;
  /**
   * The id for the modal element.
   */
  id?: string;
  /**
   * The id for the title element.
   */
  title_id?: string;
  /** Controls whether clicking on the overlay closes the modal */
  closeOnOverlayClick?: boolean;
};

const Modal = ({ children, title, id, title_id, isOpen, close, closeOnOverlayClick = true }: Props) => {
  useEffect(() => {
    const onEscapeKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close?.();
      }
    };
    document.addEventListener('keydown', onEscapeKeydown);
    return () => document.removeEventListener('keydown', onEscapeKeydown);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen || closeOnOverlayClick) {
      return () => {};
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, closeOnOverlayClick]);

  return (
    <Portal id="modal-root">
      {isOpen && (
        <S.ModalWrapper>
          <S.Overlay data-testid={testIds.core.modal.overlay} onClick={closeOnOverlayClick && close ? close : undefined} />
          <S.Modal id={id}>
            <S.ModalHeader>
              <span id={title_id}>{title}</span>
              <S.Icon id="modal_close" type="submit" onClick={close}>
                <Icon type="action.close" />
              </S.Icon>
            </S.ModalHeader>
            <S.ModalMain>{children}</S.ModalMain>
          </S.Modal>
        </S.ModalWrapper>
      )}
    </Portal>
  );
};

export default Modal;
